import styles from "./Modals.module.scss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Destination } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { useTranslation } from "react-i18next";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { observer } from "mobx-react-lite";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import omit from "lodash/omit";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import { Switch } from "@whatslly/ui-components/v2/BaseElements/Switch";
import { QuestionMarkIcon } from "@whatslly/ui-components/QuestionMarkIcon";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
const AZURE_REGION_LIST = [
  { label: "Australia East", value: "australiaeast" },
  { label: "Brazil South", value: "brazilsouth" },
  { label: "Canada Central", value: "canadacentral" },
  { label: "Central India", value: "centralindia" },
  { label: "Central US", value: "centralus" },
  { label: "Central US EUAP", value: "centraluseuap" },
  { label: "East Asia", value: "eastasia" },
  { label: "East US", value: "eastus" },
  { label: "East US 2", value: "eastus2" },
  { label: "East US 2 EUAP", value: "eastus2euap" },
  { label: "France Central", value: "francecentral" },
  { label: "Germany West Central", value: "germanywestcentral" },
  { label: "Japan East", value: "japaneast" },
  { label: "Japan West", value: "japanwest" },
  { label: "Jio India West", value: "jioindiawest" },
  { label: "Korea Central", value: "koreacentral" },
  { label: "North Central US", value: "northcentralus" },
  { label: "North Europe", value: "northeurope" },
  { label: "Norway East", value: "norwayeast" },
  { label: "Qatar Central", value: "qatarcentral" },
  { label: "South Africa North", value: "southafricanorth" },
  { label: "South Central US", value: "southcentralus" },
  { label: "Southeast Asia", value: "southeastasia" },
  { label: "Sweden Central", value: "swedencentral" },
  { label: "Switzerland North", value: "switzerlandnorth" },
  { label: "Switzerland West", value: "switzerlandwest" },
  { label: "UAE North", value: "uaenorth" },
  { label: "UK South", value: "uksouth" },
  { label: "West Central US", value: "westcentralus" },
  { label: "West Europe", value: "westeurope" },
  { label: "West US", value: "westus" },
  { label: "West US 2", value: "westus2" },
  { label: "West US 3", value: "westus3" },
];

const LANG_OPTIONS = [
  { label: "Afrikaans (South Africa)", value: "af-ZA" },
  { label: "Albanian (Albania)", value: "sq-AL" },
  { label: "Amharic (Ethiopia)", value: "am-ET" },
  { label: "Arabic (Algeria)", value: "ar-DZ" },
  { label: "Arabic (Bahrain)", value: "ar-BH" },
  { label: "Arabic (Egypt)", value: "ar-EG" },
  { label: "Arabic (Iraq)", value: "ar-IQ" },
  { label: "Arabic (Israel)", value: "ar-IL" },
  { label: "Arabic (Jordan)", value: "ar-JO" },
  { label: "Arabic (Kuwait)", value: "ar-KW" },
  { label: "Arabic (Lebanon)", value: "ar-LB" },
  { label: "Arabic (Libya)", value: "ar-LY" },
  { label: "Arabic (Morocco)", value: "ar-MA" },
  { label: "Arabic (Oman)", value: "ar-OM" },
  { label: "Arabic (Palestinian Authority)", value: "ar-PS" },
  { label: "Arabic (Qatar)", value: "ar-QA" },
  { label: "Arabic (Saudi Arabia)", value: "ar-SA" },
  { label: "Arabic (Syria)", value: "ar-SY" },
  { label: "Arabic (Tunisia)", value: "ar-TN" },
  { label: "Arabic (UAE)", value: "ar-AE" },
  { label: "Arabic (Yemen)", value: "ar-YE" },
  { label: "Armenian (Armenia)", value: "hy-AM" },
  { label: "Azerbaijani (Azerbaijan)", value: "az-AZ" },
  { label: "Basque (Basque)", value: "eu-ES" },
  { label: "Bengali (India)", value: "bn-IN" },
  { label: "Bosnian (Bosnia and Herzegovina)", value: "bs-BA" },
  { label: "Bulgarian (Bulgaria)", value: "bg-BG" },
  { label: "Burmese (Myanmar)", value: "my-MM" },
  { label: "Catalan (Spain)", value: "ca-ES" },
  { label: "Chinese (Cantonese, Simplified)", value: "zh-CN-Cantonese" },
  { label: "Chinese (Cantonese, Traditional)", value: "zh-HK" },
  { label: "Chinese (Jilu Mandarin, Simplified)", value: "zh-CN-JiLu" },
  { label: "Chinese (Mandarin, Simplified)", value: "zh-CN" },
  { label: "Chinese (Southwestern Mandarin, Simplified)", value: "zh-CN-XiNan" },
  { label: "Chinese (Taiwanese Mandarin)", value: "zh-TW" },
  { label: "Chinese (Wu, Simplified)", value: "zh-CN-Wu" },
  { label: "Croatian (Croatia)", value: "hr-HR" },
  { label: "Czech (Czech)", value: "cs-CZ" },
  { label: "Danish (Denmark)", value: "da-DK" },
  { label: "Dutch (Belgium)", value: "nl-BE" },
  { label: "Dutch (Netherlands)", value: "nl-NL" },
  { label: "English (Australia)", value: "en-AU" },
  { label: "English (Canada)", value: "en-CA" },
  { label: "English (Ghana)", value: "en-GH" },
  { label: "English (Hong Kong SAR)", value: "en-HK" },
  { label: "English (India)", value: "en-IN" },
  { label: "English (Ireland)", value: "en-IE" },
  { label: "English (Kenya)", value: "en-KE" },
  { label: "English (New Zealand)", value: "en-NZ" },
  { label: "English (Nigeria)", value: "en-NG" },
  { label: "English (Philippines)", value: "en-PH" },
  { label: "English (Singapore)", value: "en-SG" },
  { label: "English (South Africa)", value: "en-ZA" },
  { label: "English (Tanzania)", value: "en-TZ" },
  { label: "English (United Kingdom)", value: "en-GB" },
  { label: "English (United States)", value: "en-US" },
  { label: "Estonian (Estonia)", value: "et-EE" },
  { label: "Filipino (Philippines)", value: "fil-PH" },
  { label: "Finnish (Finland)", value: "fi-FI" },
  { label: "French (Belgium)", value: "fr-BE" },
  { label: "French (Canada)", value: "fr-CA" },
  { label: "French (France)", value: "fr-FR" },
  { label: "French (Switzerland)", value: "fr-CH" },
  { label: "Galician (Galician)", value: "gl-ES" },
  { label: "Georgian (Georgia)", value: "ka-GE" },
  { label: "German (Austria)", value: "de-AT" },
  { label: "German (Germany)", value: "de-DE" },
  { label: "German (Switzerland)", value: "de-CH" },
  { label: "Greek (Greece)", value: "el-GR" },
  { label: "Gujarati (Indian)", value: "gu-IN" },
  { label: "Hebrew (Israel)", value: "he-IL" },
  { label: "Hindi (India)", value: "hi-IN" },
  { label: "Hungarian (Hungary)", value: "hu-HU" },
  { label: "Icelandic (Iceland)", value: "is-IS" },
  { label: "Indonesian (Indonesia)", value: "id-ID" },
  { label: "Irish (Ireland)", value: "ga-IE" },
  { label: "Italian (Italy)", value: "it-IT" },
  { label: "Italian (Switzerland)", value: "it-CH" },
  { label: "Japanese (Japan)", value: "ja-JP" },
  { label: "Javanese (Indonesia)", value: "jv-ID" },
  { label: "Kannada (India)", value: "kn-IN" },
  { label: "Kazakh (Kazakhstan)", value: "kk-KZ" },
  { label: "Khmer (Cambodia)", value: "km-KH" },
  { label: "Korean (Korea)", value: "ko-KR" },
  { label: "Lao (Laos)", value: "lo-LA" },
  { label: "Latvian (Latvia)", value: "lv-LV" },
  { label: "Lithuanian (Lithuania)", value: "lt-LT" },
  { label: "Macedonian (North Macedonia)", value: "mk-MK" },
  { label: "Malay (Malaysia)", value: "ms-MY" },
  { label: "Malayalam (India)", value: "ml-IN" },
  { label: "Maltese (Malta)", value: "mt-MT" },
  { label: "Marathi (India)", value: "mr-IN" },
  { label: "Mongolian (Mongolia)", value: "mn-MN" },
  { label: "Nepali (Nepal)", value: "ne-NP" },
  { label: "Norwegian (Bokmål, Norway)", value: "nb-NO" },
  { label: "Pashto (Afghanistan)", value: "ps-AF" },
  { label: "Persian (Iran)", value: "fa-IR" },
  { label: "Polish (Poland)", value: "pl-PL" },
  { label: "Portuguese (Brazil)", value: "pt-BR" },
  { label: "Portuguese (Portugal)", value: "pt-PT" },
  { label: "Punjabi (India)", value: "pa-IN" },
  { label: "Romanian (Romania)", value: "ro-RO" },
  { label: "Russian (Russia)", value: "ru-RU" },
  { label: "Serbian (Serbia)", value: "sr-RS" },
  { label: "Sinhala (Sri Lanka)", value: "si-LK" },
  { label: "Slovak (Slovakia)", value: "sk-SK" },
  { label: "Slovenian (Slovenia)", value: "sl-SI" },
  { label: "Somali (Somalia)", value: "so-SO" },
  { label: "Spanish (Argentina)", value: "es-AR" },
  { label: "Spanish (Bolivia)", value: "es-BO" },
  { label: "Spanish (Chile)", value: "es-CL" },
  { label: "Spanish (Colombia)", value: "es-CO" },
  { label: "Spanish (Costa Rica)", value: "es-CR" },
  { label: "Spanish (Cuba)", value: "es-CU" },
  { label: "Spanish (Dominican Republic)", value: "es-DO" },
  { label: "Spanish (Ecuador)", value: "es-EC" },
  { label: "Spanish (El Salvador)", value: "es-SV" },
  { label: "Spanish (Guatemala)", value: "es-GT" },
  { label: "Spanish (Guinea Ecuatorial)", value: "es-GQ" },
  { label: "Spanish (Honduras)", value: "es-HN" },
  { label: "Spanish (Mexico)", value: "es-MX" },
  { label: "Spanish (Nicaragua)", value: "es-NI" },
  { label: "Spanish (Panama)", value: "es-PA" },
  { label: "Spanish (Paraguay)", value: "es-PY" },
  { label: "Spanish (Peru)", value: "es-PE" },
  { label: "Spanish (Puerto Rico)", value: "es-PR" },
  { label: "Spanish (Spain)", value: "es-ES" },
  { label: "Spanish (United States)", value: "es-US" },
  { label: "Spanish (Uruguay)", value: "es-UY" },
  { label: "Spanish (Venezuela)", value: "es-VE" },
  { label: "Swahili (Kenya)", value: "sw-KE" },
  { label: "Swahili (Tanzania)", value: "sw-TZ" },
  { label: "Swedish (Sweden)", value: "sv-SE" },
  { label: "Tamil (India)", value: "ta-IN" },
  { label: "Telugu (India)", value: "te-IN" },
  { label: "Thai (Thailand)", value: "th-TH" },
  { label: "Turkish (Türkiye)", value: "tr-TR" },
  { label: "Ukrainian (Ukraine)", value: "uk-UA" },
  { label: "Urdu (India)", value: "ur-IN" },
  { label: "Uzbek (Uzbekistan)", value: "uz-UZ" },
  { label: "Vietnamese (Vietnam)", value: "vi-VN" },
  { label: "Welsh (United Kingdom)", value: "cy-GB" },
  { label: "Zulu (South Africa)", value: "zu-ZA" },
];

const BASE_DATA: Destination = {
  type: "azureSpeechTranscribe" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.AUDIO_TRANSCRIBES,
  settings: {
    region: "eastus",
    subscriptionKey: "",
    option: {
      type: "specificLanguage",
      languageCode: "en-US",
    },
  },
};
type FormDataType = FormDestinationDataType<"azureSpeechTranscribe">;

const getDefaultLangOpt = (settings: FormDataType | undefined) => {
  return settings?.option?.type === "specificLanguage"
    ? LANG_OPTIONS.find((opt) => settings.option.type === "specificLanguage" && opt.value === settings.option.languageCode)
    : LANG_OPTIONS.find((opt) => opt.value === "en-US");
};

export const AzureSpeechTranscribeModalForm: React.FC<DestinationFormProps<"azureSpeechTranscribe">> = observer(({ onSubmitForm, settings }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<FormDataType>>(null);
  const [regionValue, setRegionValue] = useState<string>(
    AZURE_REGION_LIST.find((opt) => opt.value === settings?.region)?.value ??
      AZURE_REGION_LIST.find((opt) => opt.value === "eastus")?.value ??
      AZURE_REGION_LIST[0].value
  );
  const [langOptValue, setLangOptValue] = useState<string>(getDefaultLangOpt(settings)?.value ?? "");
  const [enableVideoTranscriptionValue, setEnableVideoTranscriptionValue] = useState<boolean>(Boolean(settings?.enableVideoTranscription));
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const validate = async (values?: FormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
    if (!values?.region) errors.region = t("field_is_required");
    if (!values?.option) errors.option = t("field_is_required");
    if (!values?.subscriptionKey) errors.subscriptionKey = t("field_is_required");

    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as FormDataType;
    return {
      ...BASE_DATA,
      settings: {
        ...omit(data, ["languageCode", "enableVideoTranscription"]),
        region: regionValue,
        subscriptionKey: data.subscriptionKey,
        enableVideoTranscription: enableVideoTranscriptionValue,
        option: {
          type: "specificLanguage" as const,
          languageCode: langOptValue,
        },
      },
    };
  };

  useEffect(() => {
    validate(getFormValues().settings);
  }, []);

  const onSubmit = async () => {
    store.testConnection(t, getFormValues());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getFormValues());
    }
  };

  const defaultRegion = useMemo(() => {
    const selected = settings?.region && AZURE_REGION_LIST.find((opt) => opt.value === settings.region);
    if (!selected) {
      return AZURE_REGION_LIST.find((opt) => opt.value === "eastus");
    }
    return selected;
  }, [settings?.region]);

  return (
    <FormElement initialValues={settings || BASE_DATA.settings} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.language")} required />
        <FormSelect<{ label: string; value: string }>
          ignoreMenuDirection={true}
          isSearchable={true}
          name={"option"}
          paddingInline="none"
          options={LANG_OPTIONS}
          onChange={(selectedOption: (typeof LANG_OPTIONS)[number]) => setLangOptValue(selectedOption?.value)}
          defaultValue={getDefaultLangOpt(settings)}
          isClearable={false}
          required
        />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.region")} required />
        <FormSelect<{ label: string; value: string }>
          ignoreMenuDirection={true}
          isSearchable={true}
          name={"region"}
          paddingInline="none"
          onChange={(selectedOption: (typeof AZURE_REGION_LIST)[number]) => setRegionValue(selectedOption?.value)}
          options={AZURE_REGION_LIST}
          defaultValue={defaultRegion}
          isClearable={false}
          required
        />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.account_key")} required />
        <FormPasswordTextInput name={"subscriptionKey"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <div className={styles.longTranscribeLabel}>
          <Label className={styles.filedLabel} text={t("destinations_page.enable_video_transcription")} />
          <Tooltip value={t("destinations_page.enable_video_transcription_tooltip")} lineWrap>
            <span>
              <QuestionMarkIcon />
            </span>
          </Tooltip>
        </div>
        <Switch checked={enableVideoTranscriptionValue} onChange={(e) => setEnableVideoTranscriptionValue(e.target.checked)} />
      </div>

      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </FormElement>
  );
});
